import { Link } from "react-router-dom";
import { ArrowRight, Wallet, GraduationCap, CalendarClock, Sparkles, Plug } from "lucide-react";
import { useSeo } from "@/lib/seo";
import Reveal from "@/components/site/Reveal";
import { services } from "@/data/services";

const integrations = [
  {
    Icon: Wallet,
    title: "Payroll",
    body: "We help you select, implement, and maintain payroll platforms — clean source-of-truth data, accurate deductions, ROEs, and seamless handoff between HR and finance.",
    examples: ["ADP", "Ceridian Dayforce", "Payworks", "Wagepoint", "QuickBooks Payroll"],
  },
  {
    Icon: GraduationCap,
    title: "Training & LMS",
    body: "Roll out and manage learning systems your team will actually use — from AODA and respect-at-work modules to role-based skills paths and compliance tracking.",
    examples: ["BambooHR Learning", "Docebo", "LearnDash", "TalentLMS", "Litmos"],
  },
  {
    Icon: CalendarClock,
    title: "Scheduling & Time",
    body: "Smart scheduling for shift-based and hybrid teams — fair rotations, time-off workflows, and clean time-tracking that holds up under union and labour standards scrutiny.",
    examples: ["Deputy", "When I Work", "7shifts", "Humanity", "Homebase"],
  },
  {
    Icon: Sparkles,
    title: "Employee Recognition",
    body: "Stand up a recognition program that reinforces the behaviours that actually matter — peer kudos, milestone moments, and manager-led recognition tied to your values.",
    examples: ["Bonusly", "Achievers", "Kudos", "Nectar", "Workhuman"],
  },
];

export default function Services() {
  useSeo({
    title: "Services",
    description:
      "Policy development, strategic HR, union & labour relations, collective agreement negotiations, training, leadership coaching, performance planning, capacity building, and platform integrations (payroll, training, scheduling, recognition).",
    path: "/services",
  });

  return (
    <div data-testid="page-services">
      {/* Header */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p className="tpc-eyebrow">Services</p>
              <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
                The work we do, the way we do it.
              </h1>
              <p className="mt-7 text-lg text-[#4A5348] leading-relaxed">
                Every engagement is shaped to your team and the moment you’re
                in. Below is the work we do most — but the real conversation
                starts with a question, not a quote.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Asymmetric Bento Grid */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
            {services.map((s, i) => {
              const Icon = s.Icon;
              // Bento sizing pattern
              // Bento layout: widest spans for the longest titles
              const wideForLongTitle = s.title.length > 32;
              const span = wideForLongTitle
                ? "md:col-span-6"
                : i % 4 === 0
                ? "md:col-span-3"
                : i % 4 === 1
                ? "md:col-span-3"
                : i % 4 === 2
                ? "md:col-span-3"
                : "md:col-span-3";
              const dark = i === 0 || i === 6;
              return (
                <Reveal key={s.slug} delay={(i % 4) * 0.06}>
                  <article
                    data-testid={`service-${s.slug}`}
                    className={`group h-full p-8 md:p-10 border ${
                      dark
                        ? "bg-[#2B3329] border-[#2B3329] text-[#E9E5DC]"
                        : "bg-[#FDFBF7] border-[#82917B]/20 text-[#2B3329]"
                    } ${span} transition-transform duration-300 hover:-translate-y-1`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div
                        className={`h-11 w-11 flex items-center justify-center rounded-full ${
                          dark
                            ? "bg-[#82917B] text-white"
                            : "bg-[#F3EFE7] text-[#82917B]"
                        }`}
                      >
                        <Icon size={20} />
                      </div>
                      <span
                        className={`text-xs tracking-[0.2em] uppercase ${
                          dark ? "text-[#A6B19E]" : "text-[#82917B]"
                        }`}
                      >
                        0{i + 1}
                      </span>
                    </div>
                    <h2
                      className={`mt-8 font-serif text-3xl md:text-4xl leading-tight tracking-tight ${
                        dark ? "text-[#FDFBF7]" : ""
                      }`}
                    >
                      {s.title}
                    </h2>
                    <p
                      className={`mt-4 text-[15px] leading-relaxed ${
                        dark ? "text-[#C9CCC1]" : "text-[#4A5348]"
                      }`}
                    >
                      {s.long}
                    </p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Integrations */}
      <section
        data-testid="services-integrations"
        className="py-24 md:py-32 bg-[#FDFBF7] border-t border-[#82917B]/15"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#82917B]/30 bg-[#F3EFE7] text-[#5A6358] text-xs tracking-[0.2em] uppercase rounded-full">
                  <Plug size={12} />
                  Platform integrations
                </div>
                <h2 className="mt-6 font-serif text-4xl sm:text-5xl tracking-tight">
                  The tools that quietly hold your people work together.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.08}>
                <p className="text-lg text-[#4A5348] leading-relaxed">
                  Most teams don’t need another platform — they need the ones
                  they already have to actually talk to each other. We help you
                  choose, implement, and integrate the systems that run the
                  day-to-day of HR so payroll, learning, scheduling, and
                  recognition all pull from a single source of truth.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {integrations.map((int, i) => {
              const Icon = int.Icon;
              return (
                <Reveal key={int.title} delay={i * 0.06}>
                  <article
                    data-testid={`integration-${int.title
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")}`}
                    className="h-full p-8 md:p-10 border border-[#82917B]/20 bg-[#F3EFE7]/50 hover:bg-[#F3EFE7] transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="h-12 w-12 rounded-full bg-[#FDFBF7] border border-[#82917B]/30 flex items-center justify-center text-[#82917B]">
                        <Icon size={20} />
                      </div>
                      <span className="tpc-eyebrow">0{i + 1}</span>
                    </div>
                    <h3 className="mt-7 font-serif text-3xl md:text-4xl tracking-tight text-[#2B3329]">
                      {int.title}
                    </h3>
                    <p className="mt-4 text-[#4A5348] text-[15px] leading-relaxed">
                      {int.body}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2">
                      {int.examples.map((ex) => (
                        <span
                          key={ex}
                          className="px-3 py-1 text-xs tracking-wide text-[#2B3329] bg-[#FDFBF7] border border-[#82917B]/25"
                        >
                          {ex}
                        </span>
                      ))}
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2}>
            <p className="mt-10 text-sm text-[#5A6358]">
              Vendor-agnostic. We work with the tools you have and recommend
              alternatives only when the fit isn’t right.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#F3EFE7]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">
              Not sure where to start?
            </h2>
            <p className="mt-5 text-[#4A5348] text-lg max-w-2xl mx-auto">
              Most of our partnerships begin with a single conversation. Tell
              us what’s on your plate — we’ll help you sort the urgent from the
              important.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="services-cta-book"
                className="tpc-btn tpc-btn-primary"
              >
                Book a Consultation <ArrowRight size={16} />
              </Link>
              <Link
                to="/industries"
                data-testid="services-cta-industries"
                className="tpc-btn tpc-btn-secondary"
              >
                See who we serve
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
